/**
 * Tournament Store - Zustand store for tournament state
 */

import { create } from 'zustand';
import type { BoardState, Move, ClockSyncEvent, GameClockInit, TimeControlKey, ChatMessage } from '@draughts/shared';
import { resolveTimeControl, MAX_OFFERTE_PATTA_PER_PARTITA } from '@draughts/shared';
import { applyMove } from '@draughts/core';
import { api } from '../services/api';
import {
  initSocket,
  isConnected,
  setSocketAuthToken,
  ensureSocketAuth,
  joinTournamentWs,
  leaveTournamentWs,
  watchTournament as watchTournamentWs,
  unwatchTournament as unwatchTournamentWs,
  sendMove,
  resignGame,
  offerDraw as socketOfferDraw,
  acceptDraw as socketAcceptDraw,
  declineDraw as socketDeclineDraw,
} from '../services/socket';
import type { DrawnOpeningPayload, TournamentStartedData } from '../services/socket';
import { useAuthStore } from './auth-store';
import { tournamentErrorKeyFor } from '../utils/tournament-error-key';
import { TOURNAMENT_ERROR_KEYS } from '../utils/tournament-error-key';
import { chiaveRifiutoPatta } from '../utils/chiave-rifiuto-patta';
import { sbloccoPattaDopoMossa } from '../utils/sblocco-patta';
import { useChatStore } from './chat-store';
import { log } from '../utils/logger';
import { trackTournamentRoundStarted } from '../utils/analytics';
import { playMoveSound, playSound } from '../utils/audio';
import { vibrate } from '../utils/vibration';
import type {
  TournamentListItem,
  TournamentStanding,
  MyParticipant,
  TournamentMatchData,
  ForcedOpeningData,
  TournamentStandingsUpdate,
  TournamentEndedData,
  TournamentGameEntry,
  TournamentDetail,
  TournamentFormat,
} from '../types/tournament';

/** Clock state for tournament games */
export interface TournamentClockState {
  white: number;
  black: number;
  activePlayer: 'white' | 'black' | null;
  lastSyncAt: number | null;
  increment: number;
}

export type TournamentScreenStatus =
  | 'browsing'        // Viewing tournament list
  | 'viewing'         // Viewing tournament details
  | 'joined'          // Joined but tournament not started
  | 'waiting'         // In active tournament, waiting for pairing
  | 'round-waiting'   // Swiss: waiting for next round to start
  | 'match-found'     // Match found, showing overlay before game
  | 'playing'         // In a tournament game
  | 'finished';       // Tournament has ended

interface TournamentStore {
  // Screen state
  status: TournamentScreenStatus;
  error: string | null;

  // Tournament list
  tournaments: TournamentListItem[];
  isLoading: boolean;

  // Current tournament
  currentTournament: TournamentListItem | null;
  standings: TournamentStanding[];
  allParticipants: TournamentStanding[];
  participantCount: number;
  isJoined: boolean;
  /** Il torneo a cui si e' iscritti, che puo' NON essere quello in pagina. */
  torneoIscrittoId: string | null;
  /** Il turno in cui si riposa, se il server lo ha annunciato. */
  turnoDiRiposo: number | null;
  /**
   * Quante partite del turno sono concluse, dall'ultimo `swiss:round-progress`.
   * `null` finche' il server non l'ha detto per il turno in corso.
   */
  avanzamentoTurno: { round: number; completate: number; totali: number } | null;
  /** I punti del riposo annunciati dal server: 2, o 4 in un turno a due manche. */
  puntiDelRiposo: number | null;
  /**
   * Quando comincia il RITORNO, in uno svizzero a due manche (migr. 105).
   *
   * `null` quando non c'e' nessun ritorno in arrivo. Esiste per una cosa
   * sola: chi ha appena finito l'andata non deve leggere «in attesa del
   * prossimo turno» — il turno e' lo stesso, e fra pochi secondi si rigioca
   * contro lo stesso avversario a colori invertiti. Si spegne da se' quando la
   * partita nuova comincia.
   */
  ritornoInArrivo: number | null;
  myParticipant: MyParticipant | null;
  myRank: number | null;

  // Game state (when playing)
  currentGameId: string | null;
  currentRoomId: string | null;
  boardState: BoardState | null;
  lastMove: Move | null;
  // Pre-move board snapshot for rolling back an optimistic move the server rejects
  preMoveBoardState: BoardState | null;
  playerColor: 'white' | 'black' | null;
  /* `rating` annullabile, e non e' un vezzo: un avversario senza punteggio
     esiste (colonna vuota), e il ripiego che copriva quel caso era uno **0**
     stampato accanto al nome. Chi lo mostra tace sul nulla. */
  opponent: { id: string; username: string; rating: number | null; avatar?: string | null } | null;
  gameResult: 'white' | 'black' | 'draw' | null;
  gameEndReason: string | null;
  ratingChange: { oldRating: number; newRating: number } | null;

  // Clock state
  clockState: TournamentClockState | null;
  timeControl: TimeControlKey | null;

  // Tournament games (for Games tab)
  tournamentGames: TournamentGameEntry[];
  gamesFilter: 'all' | 'mine';

  // Match found overlay
  matchFoundData: TournamentMatchData | null;
  /**
   * L'apertura sorteggiata e la manche della partita IN CORSO.
   *
   * Sono separati da `matchFoundData` perche' quello si azzera appena
   * l'overlay «trovato avversario» si chiude (`confirmMatch`), mentre questi
   * due devono restare per tutta la partita: sono quello che il banner sotto
   * la scacchiera annuncia.
   *
   * Il caso per cui esistono e' il RITORNO. Parte da solo, senza overlay —
   * nello svizzero `setGameStart` salta il conto alla rovescia — quindi
   * l'apertura rigiocata non si vedeva da nessuna parte, e l'unico segnale
   * che si stesse giocando la seconda partita era il colore invertito.
   */
  aperturaDellaPartita: ForcedOpeningData | null;
  /** 1 andata, 2 ritorno. `null` quando l'incontro e' a manche unica. */
  mancheDellaPartita: number | null;
  /**
   * Quante manche ha questo incontro: 2 solo in andata e ritorno.
   *
   * E' la guardia della pastiglia, e il motivo per cui esiste: `leg` da solo
   * vale 1 anche a manche unica, e il banner — che il totale non l'aveva e
   * stampava un «2» letterale — annunciava «1/2» su OGNI partita di torneo,
   * gironi compresi, che un ritorno non ce l'hanno mai.
   */
  mancheTotaliDellaPartita: number | null;

  // Draw offer state
  drawOffered: boolean;
  opponentOfferedDraw: boolean;
  /** Transient "the opponent refused your draw" banner (offerer only). */
  drawDeclinedNotice: boolean;
  /** Vedi `multiplayer-store`: il freno alle proposte di patta, copia del server. */
  drawOffersLeft: number;
  drawMustMove: boolean;

  // Grace period (opponent disconnection)
  /**
   * C'e' una partita finita mentre si era via da mostrare: `App` porta
   * l'utente sulla schermata della partita e poi la consuma.
   */
  partitaFinitaDaMostrare: boolean;
  isOpponentDisconnected: boolean;
  opponentDisconnectedColor: 'white' | 'black' | null;
  graceTimeRemaining: number | null;
  /**
   * L'avversario non si e' mai presentato (torneo): la scheda dice «Non
   * presente» invece di «Disconnesso». Letto solo con `isOpponentDisconnected`.
   */
  opponentMaiArrivato: boolean;
  /**
   * Nessun forfait allo scadere della grazia: se non arriva o non rientra, si
   * vince quando scade il SUO orologio (torneo, prima della sua prima mossa).
   * Letto solo con `isOpponentDisconnected`.
   */
  opponentDecideLOrologio: boolean;

  // Swiss round info
  currentRound: number;
  totalRounds: number;
  /**
   * I formati sono TRE, e il girone non si puo' piu' far passare per un'arena.
   *
   * Qui c'era `'arena' | 'swiss'`, e un torneo `round_robin` finiva quindi nel
   * ramo `else` di ogni `isSwiss` — punteggi dell'arena, pastiglia «Arena»,
   * badge del turno spento. Si vedeva di rado finche' il girone era una scelta
   * rara; col formato unificato e' l'esito normale di ogni torneo sotto la
   * soglia, cioe' di quasi tutti quelli fra amici.
   */
  tournamentFormat: 'arena' | 'swiss' | 'round_robin';

  // Final standings (when finished)
  finalStandings: TournamentStanding[] | null;
  winner: { userId: string; username: string; points: number } | null;

  // Internal: abort controller for join polling loop
  _joinAbort: AbortController | null;

  // Actions
  connect: () => Promise<void>;
  loadTournaments: () => Promise<void>;
  loadTournament: (id: string) => Promise<void>;
  /** Rilegge la classifica; restituisce il dettaglio letto, o `null` se non ha letto niente. */
  refreshStandings: () => Promise<TournamentDetail | null>;
  /**
   * Rilegge la scheda in pagina e aggiorna i dati DEL TORNEO — stato, turno,
   * iscritti, classifica, vincitore — mai quelli personali. Pubblica: serve a
   * chi la guarda senza accesso, che non riceve eventi.
   */
  rileggiScheda: () => Promise<void>;
  /**
   * Il socket e' tornato: rilegge il torneo in pagina e, se il server dice che
   * ci sono iscritto e il torneo e' in corso, ci rientra (`swiss:join`).
   */
  dopoLaRiconnessione: () => Promise<void>;
  /**
   * `true` se l'iscrizione e' andata a buon fine. `false` se e' fallita OPPURE
   * se e' stata annullata da un tentativo successivo: chi chiama non deve
   * festeggiare ne' navigare in nessuno dei due casi, e leggere il solo
   * `error` non basta a distinguerli — l'annullato esce senza scriverne uno.
   */
  joinTournament: (id: string, credential?: { password?: string; token?: string }) => Promise<boolean>;
  leaveTournament: () => void;
  /**
   * Toglie lo schermo dalla partita, senza dire niente al server.
   *
   * La usa la conferma di «abbandona» dopo `resign()`: la resa e' gia' partita,
   * e qui resta solo da staccare lo stato locale da `'playing'`, o gli effetti
   * di navigazione riportano il giocatore sulla damiera che ha appena lasciato.
   * Nessuna emissione: il turno successivo lo decide il torneo, non noi.
   */
  tornaAllaLobby: () => void;
  makeMove: (move: Move) => void;
  resign: () => void;
  offerDraw: () => void;
  acceptDraw: () => void;
  declineDraw: () => void;
  reset: () => void;
  loadTournamentGames: () => Promise<void>;
  setGamesFilter: (filter: 'all' | 'mine') => void;
  addCompletedGame: (game: TournamentGameEntry) => void;
  confirmMatch: () => void;

  // Clock actions
  initClock: (clockInit: GameClockInit) => void;
  syncClock: (sync: ClockSyncEvent) => void;
  tickClock: () => void;

  // Swiss round setters
  setRoundStart: (roundNumber: number, totalRounds: number, tournamentId?: string) => void;
  setRoundEnd: (roundNumber: number, tournamentId?: string) => void;
  /** Il turno di riposo annunciato dal server. */
  setBye: (roundNumber: number, tournamentId?: string, pointsAwarded?: number) => void;
  /** L'avanzamento del turno annunciato dal server. */
  setAvanzamentoTurno: (roundNumber: number, completate: number, totali: number, tournamentId?: string) => void;
  /** Il ritorno sta per cominciare (svizzero a due manche). */
  setRitornoInArrivo: (startsAt: number | null, tournamentId?: string) => void;

  // Internal setters (called by socket events)
  setTournamentJoined: (tournamentId: string, position: number) => void;
  setTournamentLeft: (tournamentId?: string | null) => void;
  setTournamentMatch: (data: TournamentMatchData) => void;
  setStandingsUpdate: (data: TournamentStandingsUpdate) => void;
  setTournamentStarted: (tournamentId?: string, totalRounds?: number, deciso?: { format?: TournamentFormat; twoLeg?: boolean }) => void;
  setTournamentEnded: (data: TournamentEndedData) => void;
  setGameStart: (state: BoardState, clock: GameClockInit | null) => void;
  setGameUpdate: (state: BoardState, lastMove: Move, clock: ClockSyncEvent | null, roomId?: string) => void;
  rollbackOptimisticMove: (message: string) => void;
  /** Rientro in una partita di torneo dopo una ricarica o una riconnessione. */
  rientraInPartita: (data: {
    tournamentId: string;
    roomId: string;
    state: BoardState;
    color: 'white' | 'black';
    clock: ClockSyncEvent | null;
    opponent: { id: string; username: string; rating: number | null; avatar?: string | null } | null;
    chat?: ChatMessage[];
    /**
     * La cadenza. Serve per l'INCREMENTO, che `clock` non porta: senza,
     * `syncClock` lo rimetteva a zero e da li' in poi nessuna mossa veniva piu'
     * pagata. Dalla chiave si ricava tutto (`resolveTimeControl`).
     */
    timeControl?: string | null;
    /** L'apertura sorteggiata, nella forma di `game:start`; assente senza apertura. */
    opening?: DrawnOpeningPayload;
    /**
     * La manche, quando l'incontro ne ha due. L'accoppiamento la annuncia una
     * volta sola e il ritorno non la rimanda: senza, un ricarico — o anche solo
     * un ritorno in primo piano — faceva sparire il «2/2» dal cartellino.
     */
    manche?: { leg: number; totalLegs: number };
    /**
     * Le NEGOZIAZIONI pendenti e il freno della patta, come nel gemello online.
     *
     * Questa lista di campi e' esplicita, e questi tre non c'erano: il rientro
     * del multiplayer le riportava da sempre, quello di torneo no. Quindi un
     * ricarico in torneo perdeva la proposta pendente mentre il SERVER la
     * teneva, e il bottone mentiva a entrambi.
     */
    drawOfferBy?: 'WHITE' | 'BLACK' | null;
    takebackOffer?: { color: 'WHITE' | 'BLACK'; toPly: number } | null;
    offertePatta?: { restanti: number; deviMuovere: boolean };
  }) => void;
  /**
   * La partita di torneo e' FINITA mentre si era via (persa per
   * disconnessione, o per tempo senza essersi presentati): la si mostra nella
   * schermata del torneo, col suo esito, invece che come una partita online
   * qualunque. `partitaFinitaDaMostrare` chiede ad `App` di portarci l'utente.
   */
  mostraPartitaFinitaMentreEriVia: (data: {
    tournamentId: string;
    roomId: string;
    state: BoardState;
    color: 'white' | 'black';
    opponent: { id: string; username: string; rating: number | null; avatar?: string | null } | null;
    chat?: ChatMessage[];
    timeControl?: string | null;
    result: 'white' | 'black' | 'draw';
    reason: string;
    /** L'apertura della partita finita: la scheda Mosse la mostra anche a fine partita. */
    opening?: DrawnOpeningPayload;
  }) => void;
  /** `App` ha portato l'utente sulla partita: la richiesta e' esaurita. */
  consumaPartitaFinitaDaMostrare: () => void;
  setGameEnd: (result: 'white' | 'black' | 'draw', reason: string, myRatingChange?: { oldRating: number; newRating: number }) => void;
  /**
   * Il server non conosce piu' la partita di torneo che stiamo mostrando.
   * Gemella di `rilasciaPartita` dell'arena, e serve per la stessa ragione.
   */
  rilasciaPartita: () => void;
  setError: (error: string | null) => void;
  setTournamentCancelled: (tournamentId: string, reason: 'organizer' | 'no_players') => void;
  setTournamentGameCompleted: (game: TournamentGameEntry, tournamentId?: string) => void;
  updateTournamentPlayerCount: (tournamentId: string, playerCount: number) => void;
}

/**
 * L'evento riguarda il torneo che l'utente sta guardando?
 *
 * Serve perche' si resta in DUE stanze piu' spesso di quanto sembri: aprendo la
 * scheda di un secondo torneo il client chiede di uscire dal primo, ma il server
 * RIFIUTA di far uscire chi e' iscritto. Cosi' gli eventi del torneo A
 * arrivavano mentre in pagina c'era B, e lo store li applicava alla cieca:
 * classifica, numero di iscritti, la propria posizione e i propri punti
 * riscritti sulla pagina sbagliata.
 *
 * Un evento senza `tournamentId` si lascia passare: alcuni eventi vecchi non lo
 * portano, e scartarli sarebbe peggio del rischio che coprono.
 */
function riguardaIlTorneoInPagina(
  corrente: { id: string } | null,
  tournamentId: string | undefined | null,
): boolean {
  if (!corrente || !tournamentId) return true;
  return corrente.id === tournamentId;
}

/*
 * Il torneo in pagina, e PROPRIO lui (id obbligatorio), per i fatti del
 * torneo stesso: stato, turno.
 *
 * `riguardaIlTorneoInPagina` lascia passare un evento senza id, eredita' di
 * quando i server non lo mandavano: per lo stato PERSONALE e' un rischio
 * accettato da tempo. Per quello del torneo no — scrivere «in corso» o «turno 3»
 * sulla scheda sbagliata lo vedrebbe chiunque la guardi. Qui l'id serve.
 */
function eProprioIlTorneoInPagina(
  corrente: { id: string } | null,
  tournamentId: string | undefined | null,
): boolean {
  return Boolean(corrente && tournamentId && corrente.id === tournamentId);
}

/*
 * Quanto e' avanti un torneo nella sua vita: una rilettura non lo fa tornare
 * indietro. Una risposta REST partita prima di un evento puo' arrivare dopo, e
 * riportare «in corso» un torneo che l'evento aveva gia' chiuso.
 */
const PASSO_DEL_TORNEO: Record<string, number> = { scheduled: 0, active: 1, finished: 2, cancelled: 2 };
const passoDelTorneo = (stato: string | undefined): number => PASSO_DEL_TORNEO[stato ?? ''] ?? 0;

/** La chiave i18n che dice perche' un torneo e' stato annullato. */
function chiaveDiAnnullamento(motivo: string | null | undefined): string {
  return motivo === 'no_players'
    ? 'tournament.errors.cancelledNoPlayers'
    : 'tournament.errors.cancelledByOrganizer';
}

/*
 * Il torneo riletto dalla REST, cucito su quello in memoria senza perdere
 * quello che gli eventi avevano gia' detto: `null` se la lettura e' rimasta
 * indietro (va buttata), altrimenti il torneo letto col turno piu' alto e il
 * vincitore, se la lettura non lo porta ancora.
 */
function torneoRiletto(corrente: TournamentListItem | null, letto: TournamentListItem): TournamentListItem | null {
  if (!corrente || corrente.id !== letto.id) return letto;
  if (passoDelTorneo(letto.status) < passoDelTorneo(corrente.status)) return null;
  return {
    ...letto,
    currentRound: Math.max(letto.currentRound ?? 0, corrente.currentRound ?? 0),
    winnerUsername: letto.winnerUsername ?? corrente.winnerUsername ?? null,
  };
}

/**
 * L'evento riguarda il torneo in cui STO GIOCANDO?
 *
 * Non e' la stessa domanda di sopra, ed e' l'errore in cui e' facile cadere: il
 * torneo «in pagina» e' la scheda che sto leggendo, non quello a cui sono
 * iscritto. Fra un turno e l'altro si va a curiosare la scheda di un altro
 * torneo — e' uno scenario normale, tant'e' che il server rifiuta di farci
 * uscire dalla stanza del nostro.
 *
 * Con il solo filtro «in pagina», da quel momento il badge del MIO turno si
 * congela e la mia posizione non si aggiorna piu': si butterebbero via
 * esattamente gli eventi che contano.
 */
function riguardaIlMioTorneo(
  iscrittoId: string | null,
  tournamentId: string | undefined | null,
): boolean {
  if (!iscrittoId || !tournamentId) return false;
  return iscrittoId === tournamentId;
}

/**
 * L'apertura del rientro nella forma di `tournament:match` (`ForcedOpeningData`).
 * `null` senza apertura o senza semi-mosse. Se e' la stessa di `attuale`, torna
 * `attuale`: il cartellino si riaccende solo a oggetto nuovo.
 */
function aperturaDalRientro(
  opening: DrawnOpeningPayload | undefined,
  attuale: ForcedOpeningData | null,
): ForcedOpeningData | null {
  if (opening?.mode !== 'drawn' || !opening.moves?.length) return null;
  const arrivata: ForcedOpeningData = {
    id: opening.id ?? 0,
    moves: opening.moves,
    /* La forza serve anche al numero: `ballotNumber` lo da' solo a una riga di
       tabella, cioe' con la forza. Le pescate casuali non la portano: ''. */
    forza: opening.forza ?? '',
    displayText: opening.displayText ?? '',
  };
  if (attuale && attuale.id === arrivata.id && attuale.moves.join(' ') === arrivata.moves.join(' ')) {
    return attuale;
  }
  return arrivata;
}

export const useTournamentStore = create<TournamentStore>((set, get) => ({
  // Initial state
  status: 'browsing',
  error: null,
  tournaments: [],
  isLoading: false,
  currentTournament: null,
  standings: [],
  allParticipants: [],
  participantCount: 0,
  isJoined: false,
  torneoIscrittoId: null,
  turnoDiRiposo: null,
  avanzamentoTurno: null,
  puntiDelRiposo: null,
  ritornoInArrivo: null,
  myParticipant: null,
  myRank: null,
  currentGameId: null,
  currentRoomId: null,
  boardState: null,
  lastMove: null,
  preMoveBoardState: null,
  playerColor: null,
  opponent: null,
  gameResult: null,
  gameEndReason: null,
  ratingChange: null,
  clockState: null,
  timeControl: null,
  tournamentGames: [],
  /* La scheda si apre su «Le mie partite», come l'arena. Chi non ha giocato
     in questo torneo le vede tutte comunque: la rete e' `hoPartite` dentro
     `scegliPartite`, non il valore di partenza. */
  gamesFilter: 'mine',
  matchFoundData: null,
  aperturaDellaPartita: null,
  mancheDellaPartita: null,
  mancheTotaliDellaPartita: null,
  drawOffered: false,
  opponentOfferedDraw: false,
  drawDeclinedNotice: false,
  drawOffersLeft: MAX_OFFERTE_PATTA_PER_PARTITA,
  drawMustMove: false,
  partitaFinitaDaMostrare: false,
  isOpponentDisconnected: false,
  opponentDisconnectedColor: null,
  graceTimeRemaining: null,
  opponentMaiArrivato: false,
  opponentDecideLOrologio: false,
  currentRound: 0,
  totalRounds: 0,
  tournamentFormat: 'arena',
  finalStandings: null,
  winner: null,
  _joinAbort: null,

  // Connect to socket server for tournaments
  connect: async () => {
    // Get auth token
    const authState = useAuthStore.getState();
    const token = authState.accessToken;

    /*
     * Anche senza token in memoria, se lo store dice che un'identita' c'e'.
     * Vedi la stessa nota in arena-store: `accessToken` non e' persistito, e la
     * guardia `if (token)` faceva nascere il socket anonimo al boot di un
     * utente registrato. Le due funzioni sanno gia' gestire il nullo.
     */
    const identitaAttesa = token || authState.isAuthenticated;

    // Ensure socket has correct auth (forces reconnect if needed)
    if (identitaAttesa) {
      await ensureSocketAuth(token);
    }

    /*
     * `initSocket` si chiama SEMPRE, anche a socket gia' collegato.
     *
     * Prima si usciva qui, e nel percorso normale il socket e' sempre gia' vivo
     * — lo apre il multiplayer al boot. Risultato: i callback di questo store
     * non venivano mai registrati. Otto si salvavano perche' li registra anche
     * il multiplayer; QUATTRO no, e sono quelli che raccontano lo svizzero:
     * `onTournamentJoined`, `onTournamentLeft`, `onTournamentRoundStart`,
     * `onTournamentRoundEnd`.
     *
     * Si vedeva: il badge «Turno 1 di 5» restava fermo per tutto il torneo, lo
     * stato «attesa fra i turni» era irraggiungibile, e soprattutto chi riceveva
     * il BYE — che il server annuncia dentro `tournament:round-start` e non con
     * un `tournament:match` — restava sui puntini di «in attesa di avversario»
     * senza sapere che quel turno riposava.
     *
     * Il registro dei callback e' unito per chiave (`socket.ts`), quindi
     * registrare di nuovo sovrascrive e non accumula: nessun ascoltatore doppio.
     * E' esattamente quello che fa l'arena, con la stessa nota.
     */
    if (!isConnected() && identitaAttesa) {
      setSocketAuthToken(token);
    }

    // Initialize socket with tournament callbacks
    /*
     * Niente `onConnect`/`onDisconnect` qui: il registro dei callback e' unito
     * per chiave, quindi due gestori vuoti registrati da questo store
     * cancellavano quelli del multiplayer — cioe' `checkActiveGame`, l'unico
     * rientro automatico in partita dell'app, e lo stato 'disconnected'. Chi
     * rientrava dopo che il socket era caduto restava con lo store fermo a
     * «partita in corso» su una damiera che non lo era piu'. E' la stessa nota
     * (e lo stesso difetto) gia' scritta in `arena-store`.
     */
    /*
     * Niente `onGameStart`/`onGameUpdate`/`onGameEnd`/`onClockSync` qui: gli
     * eventi di PARTITA arrivano instradati dal multiplayer store
     * (`multiplayer-store.ts`, che li smista su torneo/arena/lobby guardando lo
     * stato), esattamente come fa già l'arena — e per la stessa ragione, che
     * l'arena aveva scritto accanto al proprio `initSocket` e qui non era stata
     * applicata.
     *
     * Il registro dei callback è unito PER CHIAVE
     * (`socket.ts`, `callbacks = { ...callbacks, ...freshCallbacks }`) e la
     * fusione avviene PRIMA dell'uscita anticipata su socket già connesso:
     * quindi registrare queste quattro chiavi da qui le strappava al
     * multiplayer per il resto della sessione. Bastava aprire un torneo con il
     * socket giù (al boot, dopo i 15 tentativi di riconnessione, o dopo
     * un'eviction) perché `multiplayer-store.onGameStart` non esistesse più: il
     * giocatore si sedeva poi a un tavolo di lobby, il server accoppiava, e la
     * schermata non cambiava mai mentre l'orologio scorreva sul server.
     * `disconnectSocket()` non ripulisce `callbacks`, quindi la sovrascrittura
     * sopravviveva anche a logout e cambio identità.
     *
     * ⚠️ ATTENZIONE, e la nota qui sopra era VERA fino all'11 settembre: questo
     * store chiamava `initSocket` solo a socket giù, quindi non poteva strappare
     * niente a nessuno. Adesso lo chiama SEMPRE — l'uscita anticipata è stata
     * tolta perché i suoi quattro callback non venivano mai registrati — e
     * quindi registrare qui una di quelle chiavi di PARTITA la strapperebbe al
     * multiplayer per il resto della sessione. La protezione non è più
     * strutturale: è questa nota.
     */
    initSocket({
      onTournamentJoined: (data) => {
        get().setTournamentJoined(data.tournamentId, data.position);
      },
      onTournamentLeft: (data?: { tournamentId: string | null }) => {
        get().setTournamentLeft(data?.tournamentId);
      },
      onTournamentMatch: (data) => {
        get().setTournamentMatch(data);
      },
      // Gli eventi della STANZA del torneo: uguali per chi gioca e per chi
      // guarda, e definiti una volta sola (vedi `callbackDellaStanzaTorneo`).
      ...callbackDellaStanzaTorneo(),
      onTournamentError: (error) => {
        /*
         * `error` è una CHIAVE i18n da qui in poi, non la frase del server.
         *
         * Il server manda un `code` stabile e una frase inglese scritta per i log:
         * la frase resta nei log, a schermo va la traduzione scelta sul codice. È lo
         * stesso patto che l'arena ha già (`arena-error-key.ts`, il cui commento lo
         * spiega); nei tornei mancava, e le quattro schermate rendevano `{error}`
         * NUDO — un giocatore con l'app in polacco leggeva «Tournament not found» in
         * mezzo a una pagina polacca.
         */
        /*
         * Un errore disfa anche lo stato ottimistico che l'azione si era data.
         *
         * L'uscita scrive lo stato PRIMA di sapere l'esito. Se
         * il server rifiuta — «non puoi ritirarti a turni iniziati», «non sei
         * iscritto» — il messaggio arrivava ma lo schermo restava sulla bugia:
         * «non sei piu' iscritto» con l'iscrizione viva a database, o i puntini
         * di «in attesa di avversario» per sempre, senza essere in nessuna vasca.
         */
        get().setError(tournamentErrorKeyFor(error));
        const codice = typeof error === 'string' ? error : (error as { code?: string })?.code;
        if (codice === 'ROUNDS_STARTED' || codice === 'NOT_IN_TOURNAMENT') {
          const corrente = get().currentTournament?.id;
          if (corrente) void get().loadTournament(corrente);
        }
      },
      onTournamentCreated: () => {
        // Reload tournament list when a new tournament is created
        get().loadTournaments();
      },
      onTournamentBye: (data) => {
        get().setBye(data.roundNumber, data.tournamentId, data.pointsAwarded);
      },
      onSwissReturnLegPending: (data) => {
        /* Sull'orologio di QUESTO dispositivo: `startsAt` e' l'ora del server,
           e un telefono avanti o indietro di qualche secondo sbaglierebbe il
           conto alla rovescia del ritorno di altrettanto. */
        const scarto = typeof data.serverTime === 'number' ? Date.now() - data.serverTime : 0;
        get().setRitornoInArrivo(data.startsAt + scarto, data.tournamentId);
      },
      // Note: onPlayerDisconnected/onPlayerReconnected are routed centrally
      // from multiplayer-store (same pattern as onGameStart/onGameEnd)
    });
  },

  // Load tournament list
  loadTournaments: async () => {
    log.info('Tournament', 'loadTournaments() called');
    set({ isLoading: true, error: null });
    try {
      const { tournaments } = await api.getTournaments();
      log.info('Tournament', 'loadTournaments() loaded', { count: tournaments.length });
      set({ tournaments, isLoading: false });
    } catch (err) {
      set({
        // Chiave i18n, non la frase del server: vedi la nota su `onTournamentError`.
        error: tournamentErrorKeyFor(err),
        isLoading: false,
      });
    }
  },

  // Load single tournament details
  loadTournament: async (id: string) => {
    log.info('Tournament', 'loadTournament() called', { tournamentId: id });
    // Unwatch previous tournament if any
    const prev = get().currentTournament;
    if (prev && prev.id !== id) {
      unwatchTournamentWs(prev.id);
    }

    /*
     * Uno stato di PARTITA non si tocca caricando i dati del torneo.
     *
     * Questa funzione viene chiamata anche mentre si sta giocando — dal rientro
     * dopo una ricarica, per esempio — e scriveva `status` due volte: subito
     * `'viewing'`, e di nuovo alla risposta della rete. Il secondo arrivava un
     * decimo di secondo DOPO che il rientro aveva messo `'playing'`, e lo
     * cancellava. Conseguenze, tutte a schermo: l'orologio si fermava (`syncClock`
     * e `tickClock` escono se lo stato non e' `playing`) mentre sul server
     * continuava a scorrere, la damiera diventava non toccabile, e il tasto
     * Indietro smetteva di chiedere conferma su una partita in corso.
     *
     * L'arena ha la stessa guardia, con lo stesso elenco di stati da preservare.
     */
    const STATI_DI_PARTITA = ['playing', 'match-found'] as const;
    const daPreservare = (s: TournamentScreenStatus) =>
      (STATI_DI_PARTITA as readonly string[]).includes(s);

    const statoPrima = get().status;
    set({
      isLoading: true,
      error: null,
      ...(daPreservare(statoPrima) ? {} : { status: 'viewing' as const }),
    });
    try {
      const detail = await api.getTournament(id);
      const statoOra = get().status;
      set({
        currentTournament: detail.tournament,
        standings: detail.standings,
        allParticipants: detail.allParticipants,
        participantCount: detail.participantCount,
        isJoined: detail.isJoined,
        // L'iscrizione segue il torneo, non la pagina: se carico la scheda di un
        // altro torneo e NON sono iscritto li', quella di prima resta valida.
        torneoIscrittoId: detail.isJoined ? id : get().torneoIscrittoId,
        myParticipant: detail.myParticipant,
        myRank: (() => {
          const userId = useAuthStore.getState().user?.id;
          const entry = userId ? detail.standings.find(s => s.userId === userId) : null;
          return entry?.rank ?? null;
        })(),
        isLoading: false,
        // Si ricontrolla DOPO l'await: fra la richiesta e la risposta puo' essere
        // arrivato un accoppiamento, o il rientro in una partita.
        ...(daPreservare(statoOra) ? {} : {
          status: detail.isJoined
            ? (detail.tournament.status === 'active' ? 'waiting' as const : 'joined' as const)
            : 'viewing' as const,
        }),
        /*
         * La fine di un ALTRO torneo non deve sopravvivere al cambio di pagina.
         *
         * `finalStandings` e `winner` erano azzerati solo dal logout. Bastava
         * guardare finire un torneo e poi aprirne un altro: la fascia «Torneo
         * terminato» mostrava il vincitore del primo sulla pagina del secondo, e
         * ne sostituiva la classifica. Peggio: `setGameEnd` decide lo stato con
         * `finalStandings ? 'finished' : 'waiting'`, quindi il torneo nuovo
         * risultava finito appena si chiudeva una partita.
         */
        ...(get().currentTournament?.id === id ? {} : { finalStandings: null, winner: null }),
        tournamentFormat: (detail.tournament.format as 'arena' | 'swiss' | 'round_robin') || 'arena',
        currentRound: detail.tournament.currentRound || 0,
        totalRounds: detail.tournament.totalRounds || 0,
      });

      // Watch tournament room for real-time standings updates
      watchTournamentWs(id);

      // Auto-rejoin active tournament socket room (e.g. after page reload)
      if (detail.isJoined && detail.tournament.status === 'active') {
        await get().connect();
        try {
          await joinTournamentWs(id);
        } catch {
          // Already joined is fine
        }
      }
    } catch (err) {
      set({
        error: tournamentErrorKeyFor(err),
        isLoading: false,
      });
    }
  },

  // Lightweight standings refresh (no status change)
  refreshStandings: async () => {
    log.info('Tournament', 'refreshStandings() called');
    if (!useAuthStore.getState().isAuthenticated) return null;
    const { currentTournament } = get();
    if (!currentTournament) return null;
    try {
      const detail = await api.getTournament(currentTournament.id);
      const userId = useAuthStore.getState().user?.id;
      /*
       * «I miei numeri» si leggono dalla classifica solo se sono ancora
       * ISCRITTO. Una rilettura che ci contiene ancora — il server indietro di
       * un istante subito dopo un'uscita, o una risposta vecchia — ci
       * ridisegnava punti e posizione addosso, cioe' ricostruiva pezzo per
       * pezzo lo stato «iscritto» che si era appena smesso di essere.
       */
      const myEntry = userId && get().isJoined
        ? detail.standings.find(s => s.userId === userId)
        : null;
      set({
        standings: detail.standings,
        allParticipants: detail.allParticipants,
        participantCount: detail.participantCount,
        myParticipant: myEntry ? {
          points: myEntry.points,
          gamesPlayed: myEntry.gamesPlayed,
          gamesWon: myEntry.gamesWon,
          gamesDrawn: myEntry.gamesDrawn,
          gamesLost: myEntry.gamesLost,
          currentStreak: myEntry.currentStreak,
          bestStreak: 0,
          status: 'active' as const,
        } : get().myParticipant,
        myRank: myEntry?.rank ?? get().myRank,
      });
      return detail;
    } catch (err) {
      log.error('Tournament', 'refreshStandings() failed', err);
      return null;
    }
  },

  rileggiScheda: async () => {
    const id = get().currentTournament?.id;
    if (!id) return;
    try {
      const detail = await api.getTournament(id);
      // La pagina puo' essere cambiata mentre la risposta viaggiava.
      const prima = get().currentTournament;
      if (prima?.id !== id) return;
      const torneo = torneoRiletto(prima, detail.tournament);
      if (!torneo) return;
      set({
        currentTournament: torneo,
        standings: detail.standings,
        allParticipants: detail.allParticipants,
        participantCount: detail.participantCount,
        /* Chi non riceve eventi non riceve nemmeno `tournament:cancelled`: senza
           la chiave, la scheda diceva «Terminato» a un torneo annullato. */
        ...(torneo.status === 'cancelled' && prima.status !== 'cancelled'
          ? { error: chiaveDiAnnullamento(torneo.cancelReason) } : {}),
      });
    } catch (err) {
      log.warn('Tournament', 'rilettura della scheda fallita', { tournamentId: id, err });
    }
  },

  /*
   * IL RIENTRO DOPO UNA RICONNESSIONE.
   *
   * Al ritorno del socket il client rientrava nella stanza del torneo solo come
   * spettatore (`tournament:watch`, `riagganciaTorneo` in socket.ts). Due
   * conseguenze:
   *
   * 1. Gli aggiornamenti persi mentre la rete era giu' restavano persi: il
   *    server non manda la classifica a chi rientra, e nessuno la chiedeva.
   *    Pagina del torneo e classifica in partita restavano indietro fino al
   *    prossimo invio (la lobby si salvava col suo giro di 30 secondi).
   * 2. Chi e' iscritto non tornava «nel torneo» per il server: la sua riga
   *    restava «Disconnesso» per tutti, e una seconda caduta non veniva piu'
   *    segnalata, perche' la connessione nuova non era registrata.
   *
   * La rilettura viene PRIMA del rientro, ed e' lei a deciderlo: si rientra
   * solo se il server dice ADESSO che sono iscritto e che il torneo e' in
   * corso. Fidarsi dello stato in memoria vorrebbe dire rimandare un
   * `swiss:join` per un torneo da cui si e' stati ritirati mentre si era via —
   * e quello, sul server, e' il ramo della NUOVA iscrizione.
   */
  dopoLaRiconnessione: async () => {
    const torneo = get().currentTournament;
    if (!torneo) return;
    const detail = await get().refreshStandings();
    if (!detail || get().currentTournament?.id !== torneo.id) return;
    /*
     * Anche lo stato del torneo, non solo la classifica: un avvio, un turno o
     * un annullamento passati mentre la rete era giu' non tornano piu' come
     * eventi, e la scheda restava indietro. Il dettaglio e' gia' qui.
     * Senza tornare indietro, pero': vedi `torneoRiletto`.
     */
    const prima = get().currentTournament;
    const riletto = torneoRiletto(prima, detail.tournament);
    if (riletto) {
      set({
        currentTournament: riletto,
        ...(riletto.status === 'cancelled' && prima?.status !== 'cancelled'
          ? { error: chiaveDiAnnullamento(riletto.cancelReason) } : {}),
      });
    }
    if (!detail.isJoined || detail.tournament.status !== 'active') return;
    try {
      await joinTournamentWs(torneo.id);
    } catch (err) {
      log.warn('Tournament', 'rientro nel torneo dopo la riconnessione fallito', { tournamentId: torneo.id, err });
    }
  },

  // Join tournament
  joinTournament: async (id: string, credential?: { password?: string; token?: string }): Promise<boolean> => {
    log.info('Tournament', 'joinTournament() called', { tournamentId: id });
    // Abort any previous join attempt to prevent concurrent polling loops
    if (get()._joinAbort) {
      get()._joinAbort!.abort();
    }
    const abortController = new AbortController();
    /*
     * L'errore si azzera all'INIZIO, non solo in caso di successo.
     *
     * Chi guarda l'esito di questo tentativo lo legge da `error` appena finita
     * l'attesa, e un errore vecchio rimasto in memoria si fa scambiare per il
     * verdetto di adesso. Capita davvero sul ramo silenzioso qui sopra: un
     * doppio tocco fa annullare il primo tentativo, che esce senza toccare
     * `error`, e chi lo aveva avviato leggerebbe il rifiuto di prima —
     * buttando via, per esempio, un gettone d'invito ancora buono.
     */
    set({ _joinAbort: abortController, error: null });

    try {
      // Ensure socket is connected with proper auth
      await get().connect();

      // Wait for socket to be connected (max 3 seconds)
      let attempts = 0;
      while (!isConnected() && attempts < 30) {
        if (abortController.signal.aborted) return false;
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (abortController.signal.aborted) return false;

      if (!isConnected()) {
        throw new Error('Could not connect to server');
      }

      // Join via WebSocket for real-time updates
      const result = await joinTournamentWs(id, credential);

      // Reload tournament details and set isJoined in a single batch to avoid layout jump
      const detail = await api.getTournament(id);
      set({
        isJoined: true,
        error: null,
        currentTournament: detail.tournament,
        standings: detail.standings,
        allParticipants: detail.allParticipants,
        participantCount: detail.participantCount,
        myParticipant: detail.myParticipant,
        myRank: (() => {
          const userId = useAuthStore.getState().user?.id;
          const entry = userId ? detail.standings.find((s: { userId: string }) => s.userId === userId) : null;
          return entry?.rank ?? null;
        })(),
        status: detail.tournament.status === 'active' ? 'waiting' : 'joined',
      });
      log.info('Tournament', 'joinTournament() success', { tournamentId: id });
      return true;
    } catch (err) {
      log.error('Tournament', 'joinTournament() failed', err);
      set({
        error: tournamentErrorKeyFor(err),
      });
      return false;
    }
  },

  /*
   * Uscire dal torneo. La propria riga sparisce SUBITO, e la rilettura la fa
   * `setTournamentLeft` — cioe' la conferma del server.
   *
   * Qui c'era una `api.getTournament()` lanciata nello stesso tick
   * dell'emissione, e l'emissione non ha ack: quella GET poteva leggere
   * `tournament_participants` PRIMA che il server scrivesse `withdrawn`, e la
   * sua risposta — che arriva dopo il broadcast della classifica nuova, perche'
   * il dettaglio fa piu' viaggi al database dell'uscita — rimetteva in pagina la
   * fotografia di prima. Era il difetto che si vedeva: bottone cambiato, nome
   * ancora in classifica, e nessun altro evento a smentirlo.
   *
   * (Il commento che stava qui diceva anche che il socket, uscendo, non riceve
   * il broadcast. Non e' vero: `handleSwissLeave` trasmette la classifica PRIMA
   * di `socket.leave`, quindi chi esce quell'ultimo aggiornamento lo riceve.)
   */
  leaveTournament: () => {
    log.info('Tournament', 'leaveTournament() called');
    const tournamentId = get().currentTournament?.id;

    /*
     * A socket giu' non ci si dichiara usciti.
     *
     * `leaveTournamentWs` esce in silenzio se la connessione non c'e', e senza
     * piu' una rilettura a smentire lo schermo direbbe «non sei iscritto» con
     * l'iscrizione viva a database — la bugia da cui nascono i forfait di chi
     * credeva di essersi cancellato. Meglio un errore e lo stato fermo.
     */
    if (!isConnected()) {
      log.warn('Tournament', 'uscita non inviata: socket non connesso');
      set({ error: 'tournament.errors.offline' });
      return;
    }

    leaveTournamentWs(tournamentId);
    useChatStore.getState().clearChatContext();

    const userId = useAuthStore.getState().user?.id;
    set((stato) => ({
      isJoined: false,
      myParticipant: null,
      myRank: null,
      status: 'viewing',
      error: null,
      // L'iscrizione era a QUESTO torneo: senza azzerarla la pagina non fa piu'
      // l'`unwatch` uscendo (`siEsceDallaStanzaDelTorneo`), e i propri numeri
      // continuerebbero a farsi riscrivere dagli eventi di un torneo lasciato.
      torneoIscrittoId: stato.torneoIscrittoId === tournamentId ? null : stato.torneoIscrittoId,
      /*
       * La propria riga via subito, senza aspettare la rete. I `rank` rimasti
       * NON si rinumerano: prima dell'avvio sono tutti primi a pari merito —
       * nessuno ha giocato — quindi non resta nessun buco, e a torneo avviato
       * la numerazione autorevole (ex aequo compresi) e' quella che il server
       * rimanda col broadcast un istante dopo.
       */
      ...(userId ? {
        standings: stato.standings.filter((r) => r.userId !== userId),
        allParticipants: stato.allParticipants.filter((r) => r.userId !== userId),
        participantCount: Math.max(0, stato.participantCount - 1),
      } : {}),
    }));
  },

  /*
   * Solo lo stato locale, nessuna emissione.
   *
   * Qui prima si chiamava `pauseSearch()`, che mandava `tournament:pause` al
   * server. Non era una pausa chiesta da nessuno: serviva a staccarsi da
   * `'playing'`, o gli effetti di navigazione riportavano il giocatore sulla
   * damiera appena lasciata. Il server pero' quell'evento lo RIFIUTA sempre nei
   * formati a turni — `handleTournamentJoin` esce dentro `handleSwissJoin`
   * prima di registrare il socket in `socketTournaments`, quindi
   * `handleTournamentPause` cadeva sempre nel ramo di ripiego, dove la guardia
   * `PAUSE_NOT_AVAILABLE` c'e' — e il rifiuto diventava una fascia rossa: «In
   * un torneo a turni non ci si puo' mettere in pausa», in faccia a chi aveva
   * solo abbandonato una partita.
   *
   * ⚠️ Un torneo `format='arena'` (il CHECK della migration 089 li ammette
   * ancora, anche se non se ne crea piu' nessuno) passa di qui lo stesso, e per
   * lui l'emissione faceva un lavoro vero: `removeFromPool`. Senza, resta nella
   * vasca e puo' essere riaccoppiato subito. E' il solo caso in cui questa
   * modifica toglie qualcosa, e vale il prezzo.
   *
   * `'round-waiting'` e non `'waiting'`: fra un turno e l'altro non si sta
   * cercando un avversario, si aspetta il turno.
   */
  tornaAllaLobby: () => {
    log.info('Tournament', 'tornaAllaLobby(): lo schermo lascia la partita, il server non viene toccato');
    set({ status: 'round-waiting' });
  },

  // Make a move in current game
  makeMove: (move: Move) => {
    const { currentRoomId, status, boardState, playerColor } = get();
    if (status !== 'playing' || !currentRoomId || !boardState) {
      return;
    }

    // Check if it's our turn
    const isOurTurn =
      (playerColor === 'white' && boardState.currentTurn === 'WHITE') ||
      (playerColor === 'black' && boardState.currentTurn === 'BLACK');

    if (!isOurTurn) {
      // Chiave i18n: era una frase inglese cablata, resa nuda a schermo.
      set({ error: 'tournament.errors.notYourTurn' });
      return;
    }

    // Optimistic update: apply the move locally for instant board + sound feedback
    // instead of waiting a network round-trip for the server's game:update echo.
    // The core engine mirrors the server, so reconciliation in setGameUpdate is
    // seamless; preMoveBoardState lets rollbackOptimisticMove restore the board if
    // the server rejects the move (game:error).
    try {
      const optimistic = applyMove(boardState, move);
      set({ boardState: optimistic, lastMove: move, preMoveBoardState: boardState });
      playMoveSound(move.isPromotion, undefined, move.captures.length > 0);
      vibrate(40);
    } catch (err) {
      log.warn('Tournament', 'optimistic applyMove failed; sending without local apply', {
        from: move.from,
        to: move.to,
        err,
      });
    }

    sendMove(currentRoomId, move);
  },

  // Resign from current game
  resign: () => {
    const { currentRoomId, status } = get();
    if (currentRoomId && (status === 'playing' || status === 'match-found')) {
      resignGame(currentRoomId);
    }
  },

  // Draw offer actions
  offerDraw: () => {
    const { currentRoomId, status, drawOffered, opponentOfferedDraw, drawMustMove, drawOffersLeft } = get();
    if (status !== 'playing' || !currentRoomId || drawOffered || opponentOfferedDraw) return;
    /* Il freno (vedi `multiplayer-store.offerDraw`): il bottone e' gia' spento,
       e un giro di rete per farsi dire no e' un giro sprecato. */
    if (drawMustMove || drawOffersLeft <= 0) return;
    /* Lo stato di prima, per rimetterlo se la proposta non parte: vedi
       `multiplayer-store.offerDraw`. */
    const primaDellOfferta = { drawMustMove, drawOffersLeft };
    // A new offer clears the previous refusal (see multiplayer-store.offerDraw).
    set({
      drawOffered: true,
      drawDeclinedNotice: false,
      drawMustMove: true,
      drawOffersLeft: Math.max(0, drawOffersLeft - 1),
    });
    // Roll back the optimistic flag if the server rejects the offer (e.g. throttled),
    // otherwise the draw button stays stuck on "Draw sent" forever.
    void socketOfferDraw(currentRoomId).then((res) => {
      /* Il conto del server vince sempre; un campo assente non spegne niente. */
      if (res?.offertePatta) {
        set({ drawOffersLeft: res.offertePatta.restanti, drawMustMove: res.offertePatta.deviMuovere });
      }
      if (res?.error && get().drawOffered) {
        /* Senza il conto del server si torna a com'era: l'offerta non e'
           partita, quindi non ha speso niente e non blocca niente. */
        if (!res.offertePatta) set(primaDellOfferta);
        // `res.error` è la frase inglese dell'ack: a schermo va la traduzione.
        set({
          drawOffered: false,
          error: chiaveRifiutoPatta(res, TOURNAMENT_ERROR_KEYS, tournamentErrorKeyFor(res)),
        });
      }
    });
  },

  acceptDraw: () => {
    const { currentRoomId, status, opponentOfferedDraw } = get();
    if (status !== 'playing' || !currentRoomId || !opponentOfferedDraw) return;
    socketAcceptDraw(currentRoomId);
  },

  declineDraw: () => {
    const { currentRoomId, status, opponentOfferedDraw } = get();
    if (status !== 'playing' || !currentRoomId || !opponentOfferedDraw) return;
    socketDeclineDraw(currentRoomId);
    set({ opponentOfferedDraw: false });
  },

  // Reset to initial state
  reset: () => {
    log.info('Tournament', 'reset() called');
    // Unwatch current tournament
    const prev = get().currentTournament;
    if (prev) {
      unwatchTournamentWs(prev.id);
    }
    useChatStore.getState().clearChatContext();

    set({
      status: 'browsing',
      error: null,
      currentTournament: null,
      /* L'iscrizione se ne va col torneo, come fa l'arena con `arenaIscrittaId`:
         sopravvivendo a un `reset()` restava a dire «sono dentro quel torneo» a
         chi la legge (i filtri degli eventi, il banner della gara in corso) per
         un torneo che non c'e' piu' in memoria. Da quando l'archiviazione del
         dopo-torneo chiama questa funzione — e non solo il logout — la
         differenza si vede. */
      torneoIscrittoId: null,
      standings: [],
      allParticipants: [],
      participantCount: 0,
      isJoined: false,
      myParticipant: null,
      myRank: null,
      currentGameId: null,
      currentRoomId: null,
      boardState: null,
      lastMove: null,
      preMoveBoardState: null,
      playerColor: null,
      opponent: null,
      gameResult: null,
      gameEndReason: null,
      ratingChange: null,
      clockState: null,
      timeControl: null,
      tournamentGames: [],
      gamesFilter: 'mine',
      matchFoundData: null,
      aperturaDellaPartita: null,
      mancheDellaPartita: null,
      mancheTotaliDellaPartita: null,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
      currentRound: 0,
      totalRounds: 0,
      tournamentFormat: 'arena',
      finalStandings: null,
      winner: null,
      _joinAbort: null,
    });
  },

  // Load tournament games from API
  loadTournamentGames: async () => {
    const { currentTournament } = get();
    if (!currentTournament) return;

    try {
      // 100 e' il tetto del server (`limitePagina` nel controller): con i 50
      // di default un torneo lungo perdeva i turni piu' vecchi.
      const { games } = await api.getTournamentGames(currentTournament.id, 100);
      set({ tournamentGames: games });
    } catch (err) {
      log.error('Tournament', 'Failed to load games', err);
    }
  },

  // Set games filter
  setGamesFilter: (filter: 'all' | 'mine') => {
    set({ gamesFilter: filter });
  },

  // Add a completed game (from socket event)
  addCompletedGame: (game: TournamentGameEntry) => {
    set((state) => ({
      tournamentGames: [game, ...state.tournamentGames],
    }));
  },

  // Confirm match and transition to playing
  confirmMatch: () => {
    const { matchFoundData, currentRoomId } = get();
    if (matchFoundData) {
      /* La chiave e' la stanza: lo svizzero puo' aver gia' suonato saltando
         l'overlay (vedi `setGameStart`), e i due percorsi non si escludono. */
      playSound('match-found', currentRoomId ? { onceForKey: currentRoomId } : undefined);
      set({
        status: 'playing',
        matchFoundData: null,
      });
    }
  },

  // ========== Clock Actions ==========

  // Initialize clock from game:start event
  initClock: (clockInit: GameClockInit) => {
    if (!clockInit || clockInit.timeControl === 'none') {
      set({ clockState: null, timeControl: null });
      return;
    }

    set({
      clockState: {
        white: clockInit.white,
        black: clockInit.black,
        activePlayer: null, // Clock doesn't tick until first move
        lastSyncAt: Date.now(),
        increment: clockInit.increment,
      },
      timeControl: clockInit.timeControl,
    });
  },

  // Sync clock from server
  syncClock: (sync: ClockSyncEvent) => {
    const { status } = get();
    if (status !== 'playing') return;

    set({
      clockState: {
        white: sync.white,
        black: sync.black,
        activePlayer: sync.activePlayer,
        lastSyncAt: Date.now(),
        increment: get().clockState?.increment ?? 0,
      },
    });
  },

  // Local tick for smooth interpolation
  tickClock: () => {
    const { clockState, status } = get();
    if (!clockState || status !== 'playing' || !clockState.activePlayer || !clockState.lastSyncAt) {
      return;
    }

    const elapsed = Date.now() - clockState.lastSyncAt;
    const activePlayer = clockState.activePlayer;
    const newTime = Math.max(0, clockState[activePlayer] - elapsed);

    set({
      clockState: {
        ...clockState,
        [activePlayer]: newTime,
        lastSyncAt: Date.now(),
      },
    });
  },

  // Swiss round setters
  setRoundStart: (roundNumber: number, totalRounds: number, tournamentId?: string) => {
    if (!riguardaIlTorneoInPagina(get().currentTournament, tournamentId)
        && !riguardaIlMioTorneo(get().torneoIscrittoId, tournamentId)) return;
    log.info('Tournament', 'round started', { roundNumber, totalRounds });
    /*
     * Anche il turno del torneo in pagina: «Turno X di Y» e il «non ci si iscrive
     * piu'» della scheda leggono `currentTournament.currentRound`, che restava
     * quello dell'apertura della pagina. Un turno che parte dice anche che il
     * torneo e' in corso, nel caso l'avvio fosse andato perso.
     */
    const corrente = get().currentTournament;
    if (eProprioIlTorneoInPagina(corrente, tournamentId)) {
      const avvioPerso = corrente!.status === 'scheduled';
      set({
        currentTournament: {
          ...corrente!,
          currentRound: roundNumber,
          ...(totalRounds > 0 ? { totalRounds } : {}),
          ...(avvioPerso ? { status: 'active' as const } : {}),
        },
      });
      /*
       * Se l'avvio e' andato perso, con lui e' andato perso il FORMATO deciso:
       * un girone-svizzero resterebbe 'swiss' in pagina, e la scheda Dettagli
       * elencherebbe gli spareggi dello svizzero per un girone. L'evento del
       * turno non lo porta; la scheda riletta si'.
       */
      if (avvioPerso && corrente!.swissThreshold != null) void get().rileggiScheda();
    }
    set({
      currentRound: roundNumber,
      totalRounds,
      // Il riposo vale per un turno solo: al turno dopo si gioca.
      ...(get().turnoDiRiposo !== null && get().turnoDiRiposo !== roundNumber
        ? { turnoDiRiposo: null, puntiDelRiposo: null } : {}),
    });
    /*
     * La metrica conta i turni GIOCATI, non quelli visti: da quando la stanza
     * del torneo arriva anche a chi guarda (`callbackDellaStanzaTorneo`), senza
     * questo filtro ogni spettatore aggiungeva un turno a Umami. E il torneo e'
     * quello dell'evento, non per forza quello in pagina.
     */
    const ioGioco = riguardaIlMioTorneo(get().torneoIscrittoId, tournamentId)
      || (get().isJoined && riguardaIlTorneoInPagina(get().currentTournament, tournamentId));
    const torneoDelTurno = tournamentId ?? get().currentTournament?.id;
    if (ioGioco && torneoDelTurno) trackTournamentRoundStarted(torneoDelTurno, roundNumber);
  },

  setRoundEnd: (roundNumber: number, tournamentId?: string) => {
    if (!riguardaIlTorneoInPagina(get().currentTournament, tournamentId)
        && !riguardaIlMioTorneo(get().torneoIscrittoId, tournamentId)) return;
    log.info('Tournament', 'round ended', { roundNumber });
    const { status } = get();
    /*
     * Anche il NUMERO del turno, non solo lo stato.
     *
     * `currentRound` lo scriveva solo `setRoundStart`, cioe' solo per chi era
     * gia' nella stanza del torneo quando il turno e' partito. Chi ci entra
     * dopo quell'istante non riceve piu' `tournament:round-start` e resta a 0
     * per sempre: nessun altro evento glielo dice, e `tournament:standings`
     * non porta il numero.
     *
     * Visto il 19 Set su quattro finestre: tre leggevano «in attesa del
     * prossimo turno», la quarta «in attesa dell'inizio del turno», perche'
     * per lei il torneo non aveva ancora cominciato nessun turno.
     *
     * `round-end` porta lo stesso `roundNumber` e arriva a tutta la stanza, e
     * il server NON tocca `current_round` chiudendo il turno: il numero che
     * scriviamo qui e' esattamente quello che ha il database.
     *
     * Lo stesso per la scheda, che legge `currentTournament.currentRound`: chi
     * la apre a turno iniziato non ha sentito il `round-start`.
     */
    const corrente = get().currentTournament;
    if (eProprioIlTorneoInPagina(corrente, tournamentId) && (corrente!.currentRound ?? 0) < roundNumber) {
      set({ currentTournament: { ...corrente!, currentRound: roundNumber } });
    }
    /*
     * «In attesa del prossimo turno» e' lo stato di chi GIOCA questo torneo.
     *
     * `round-end` va a tutta la stanza, e nella stanza c'e' anche chi guarda: a
     * lui la scheda direbbe che aspetta un turno che non giochera', e resterebbe
     * cosi' anche durante il turno dopo, perche' `setRoundStart` lo stato non lo
     * tocca. Iscritto = il mio torneo, oppure quello in pagina con `isJoined`
     * (subito dopo l'iscrizione `torneoIscrittoId` puo' non essere ancora scritto).
     */
    const ioGioco = riguardaIlMioTorneo(get().torneoIscrittoId, tournamentId)
      || (get().isJoined && riguardaIlTorneoInPagina(corrente, tournamentId));
    set({
      currentRound: roundNumber,
      // Non si tocca una partita in corso: il turno chiuso riguarda il turno.
      ...(ioGioco && status !== 'playing' && status !== 'match-found'
        ? { status: 'round-waiting' as const } : {}),
    });
  },

  setBye: (roundNumber: number, tournamentId?: string, pointsAwarded?: number) => {
    /*
     * Il turno di riposo, che finora non arrivava mai a schermo.
     *
     * Il server lo annuncia da sempre al solo interessato, ma il client non
     * ascoltava quell'evento: chi riceveva il bye restava sui puntini di «in
     * attesa di avversario» per tutto il turno — senza sapere che riposava, e
     * che il punto era gia' suo.
     */
    if (!riguardaIlTorneoInPagina(get().currentTournament, tournamentId)
        && !riguardaIlMioTorneo(get().torneoIscrittoId, tournamentId)) return;
    log.info('Tournament', 'turno di riposo', { roundNumber });
    const { status } = get();
    set({
      turnoDiRiposo: roundNumber,
      puntiDelRiposo: pointsAwarded ?? null,
      // Non si tocca una partita in corso: il bye riguarda il turno, non lei.
      ...(status !== 'playing' && status !== 'match-found' ? { status: 'round-waiting' as const } : {}),
    });
  },

  setAvanzamentoTurno: (roundNumber: number, completate: number, totali: number, tournamentId?: string) => {
    /* Fra un turno e l'altro la pagina diceva solo «in attesa»: nessuno sapeva
       se mancava una partita o tutte. Il server lo mandava gia', e nessuno lo
       ascoltava. */
    if (!riguardaIlTorneoInPagina(get().currentTournament, tournamentId)
        && !riguardaIlMioTorneo(get().torneoIscrittoId, tournamentId)) return;
    set({ avanzamentoTurno: { round: roundNumber, completate, totali } });
  },

  setRitornoInArrivo: (startsAt: number | null, tournamentId?: string) => {
    if (startsAt !== null
        && !riguardaIlTorneoInPagina(get().currentTournament, tournamentId)
        && !riguardaIlMioTorneo(get().torneoIscrittoId, tournamentId)) return;
    log.info('Tournament', 'ritorno in arrivo', { startsAt });
    set({ ritornoInArrivo: startsAt });
  },

  // Internal setters for socket events
  setTournamentJoined: (tournamentId: string, position: number) => {
    set({
      isJoined: true,
      status: 'joined',
    });
  },

  setTournamentLeft: (tournamentId?: string | null) => {
    /*
     * Il server risponde con `tournamentId: null` quando NON ha trovato niente
     * da cancellare — e questa funzione lo ignorava, confermando lo stato
     * ottimistico che il client si era gia' dato. Cosi' l'unico evento che
     * poteva smentire la bugia («non sei piu' iscritto») la confermava, e il
     * giocatore restava iscritto sul database mentre lo schermo diceva di no.
     *
     * Ora un `null` rimette la verita' al suo posto: si ricarica il dettaglio,
     * che dira' com'e' andata davvero.
     */
    if (tournamentId === null) {
      log.warn('Tournament', 'il server non ha trovato nessuna iscrizione da annullare');
      const corrente = get().currentTournament?.id;
      if (corrente) void get().loadTournament(corrente);
      return;
    }
    set((stato) => ({
      isJoined: false,
      myParticipant: null,
      myRank: null,
      status: 'viewing',
      torneoIscrittoId: stato.torneoIscrittoId === tournamentId ? null : stato.torneoIscrittoId,
    }));

    /*
     * La rilettura sta QUI, non dentro `leaveTournament`: questo evento arriva
     * DOPO che il server ha scritto il ritiro, quindi la GET non puo' piu'
     * leggere la platea di prima e rimetterla in pagina. E' la corsa che
     * teneva il proprio nome in classifica dopo «Annulla iscrizione».
     *
     * Prima il rientro nella stanza: il server ci ha tolti (`socket.leave`
     * subito prima di annunciare l'uscita), e senza rientrare la classifica si
     * fermerebbe a questo istante per tutto il tempo che si resta a guardare.
     *
     * `refreshStandings` e non `loadTournament`: niente `isLoading`, quindi
     * nessun lampo di scheletro su una pagina che si sta gia' guardando.
     */
    if (tournamentId && get().currentTournament?.id === tournamentId) {
      watchTournamentWs(tournamentId);
      void get().refreshStandings();
    }
  },

  setTournamentMatch: (data: TournamentMatchData) => {
    /* Un server piu' vecchio del campo non manda il totale: una manche sola. */
    const mancheTotali = data.totalLegs ?? 1;
    // New round, new room: the previous game's chat does not follow the player
    // into the next pairing.
    useChatStore.getState().setChatContext({
      roomId: data.roomId,
      playerColor: data.color,
      canChat: true,
      source: 'tournament',
    });

    set({
      status: 'match-found',
      matchFoundData: data,
      /* La fascia del turno prima non ha un bottone per chiuderla: la chiude un
         accoppiamento nuovo, che e' il momento in cui non serve piu' (e l'unica
         che passa da qui e' quella del rilascio, `rilasciaPartita`). */
      error: null,
      /* Sopravvivono alla chiusura dell'overlay: `matchFoundData` no. */
      aperturaDellaPartita: data.forcedOpening ?? null,
      /*
       * La manche SOLO se l'incontro ne ha davvero due.
       *
       * Qui si guardava `leg > 0`, e il commento di allora («`null` a manche
       * unica») descriveva un server che non e' mai esistito: `leg` vale 1
       * anche a manche unica — lo manda ogni tabellone che si apre — quindi il
       * banner annunciava «1/2» su ogni partita di torneo. La distinzione la
       * porta il TOTALE, che ora viaggia (`totalLegs`); un server piu' vecchio
       * del campo non ne manda nessuno, e un incontro di cui non si sa il
       * totale non annuncia niente.
       */
      mancheDellaPartita: mancheTotali > 1 && data.leg ? data.leg : null,
      mancheTotaliDellaPartita: mancheTotali > 1 ? mancheTotali : null,
      currentGameId: data.gameId,
      currentRoomId: data.roomId,
      playerColor: data.color,
      opponent: {
        id: data.opponentId,
        username: data.opponentUsername,
        rating: data.opponentRating,
        avatar: data.opponentAvatar ?? null,
      },
      // Reset state for new game
      boardState: null,
      lastMove: null,
      preMoveBoardState: null,
      gameResult: null,
      gameEndReason: null,
      ratingChange: null,
    });
  },

  setStandingsUpdate: (data: TournamentStandingsUpdate) => {
    /*
     * Due domande diverse, due risposte diverse.
     *
     * La classifica disegnata a schermo e' quella del torneo IN PAGINA: gli
     * eventi di un altro non devono riscriverla. Ma «i miei punti» e «la mia
     * posizione» appartengono al torneo in cui GIOCO, che puo' non essere quello
     * che sto leggendo — fra un turno e l'altro si va a curiosare la scheda di
     * un altro torneo, e il server non ci fa nemmeno uscire dalla stanza del
     * nostro. Filtrare tutto sulla pagina congelava i miei dati; non filtrare
     * niente riscriveva la pagina sbagliata.
     */
    const perLaPagina = riguardaIlTorneoInPagina(get().currentTournament, data.tournamentId);
    const perMe = riguardaIlMioTorneo(get().torneoIscrittoId, data.tournamentId);
    if (!perLaPagina && !perMe) return;
    // Try to extract own stats from standings array using auth user id
    const userId = useAuthStore.getState().user?.id;
    const myEntry = userId ? data.standings.find((s) => s.userId === userId) : null;

    let myParticipant;
    let myRank;

    if (myEntry) {
      // Best source: find ourselves in the standings
      myParticipant = {
        points: myEntry.points,
        gamesPlayed: myEntry.gamesPlayed,
        gamesWon: myEntry.gamesWon,
        gamesDrawn: myEntry.gamesDrawn,
        gamesLost: myEntry.gamesLost,
        currentStreak: myEntry.currentStreak,
        bestStreak: 0,
        status: 'active' as const,
      };
      myRank = myEntry.rank;
    } else if (data.myStats) {
      // Fallback: server-provided stats
      myParticipant = {
        points: data.myStats.points,
        gamesPlayed: data.myStats.gamesPlayed,
        gamesWon: 0,
        gamesDrawn: 0,
        gamesLost: 0,
        currentStreak: data.myStats.streak,
        bestStreak: 0,
        status: 'active' as const,
      };
      myRank = data.myRank;
    } else {
      // Keep existing
      myParticipant = get().myParticipant;
      myRank = data.myRank ?? get().myRank;
    }

    set({
      // La graduatoria e i conteggi solo se l'evento e' della pagina.
      ...(perLaPagina ? {
        standings: data.standings,
        allParticipants: data.standings,
        participantCount: data.participantCount,
      } : {}),
      // I miei numeri solo se l'evento e' del torneo in cui gioco: leggere la
      // scheda di un altro non deve farmi ereditare la posizione di li'.
      ...(perMe || (perLaPagina && get().torneoIscrittoId === null) ? { myRank, myParticipant } : {}),
    });
  },

  setTournamentStarted: (tournamentId?: string, totalRounds?: number, deciso?: { format?: TournamentFormat; twoLeg?: boolean }) => {
    /*
     * Il torneo stesso passa a «in corso», per CHIUNQUE abbia la scheda aperta.
     *
     * Prima si aggiornava solo lo stato personale dell'iscritto: chi guardava
     * restava su «in programma», col conto alla rovescia e il bottone
     * «Iscriviti», finche' non ricaricava la pagina. La scheda legge
     * `currentTournament.status`, e nessun evento lo toccava.
     */
    const corrente = get().currentTournament;
    if (eProprioIlTorneoInPagina(corrente, tournamentId) && corrente!.status === 'scheduled') {
      set({
        currentTournament: {
          ...corrente!,
          status: 'active',
          ...(typeof totalRounds === 'number' && totalRounds > 0 ? { totalRounds } : {}),
          /* Il formato DECISO: un girone-svizzero che diventa girone non deve
             restare 'swiss' in pagina fino al ricaricamento — la scheda
             Dettagli elencherebbe gli spareggi sbagliati (RegoleDelTorneo). */
          ...(deciso?.format ? { format: deciso.format } : {}),
          ...(typeof deciso?.twoLeg === 'boolean' ? { twoLeg: deciso.twoLeg } : {}),
        },
      });
    }

    // Filtrato come gli altri: l'avvio del torneo A arrivava mentre si leggeva
    // la scheda di B, e `isJoined` — che nel frattempo si riferisce a B — faceva
    // passare per iscritto chi non lo era.
    if (!riguardaIlTorneoInPagina(get().currentTournament, tournamentId)) return;
    const { isJoined } = get();
    if (isJoined) {
      set({ status: 'waiting' });
    }
  },

  setTournamentEnded: (data: TournamentEndedData) => {
    const { status, currentTournament } = get();
    /*
     * Solo il torneo che si sta guardando.
     *
     * Si resta in due stanze piu' spesso di quanto sembri: aprendo la scheda di
     * un secondo torneo il client chiede di uscire dal primo, ma il server
     * RIFIUTA di farti uscire da quello a cui sei iscritto. Cosi' la fine del
     * torneo A arrivava mentre in pagina c'era B — e da quando questa funzione
     * scrive anche `currentTournament.status`, segnava finito B: via il conto
     * alla rovescia, via i blocchi di iscrizione, e la classifica di A nella
     * tabella di B. Il torneo a cui volevi iscriverti diventava inaccessibile
     * fino a un ricarico. L'arena questa guardia ce l'ha gia'.
     */
    if (currentTournament && data.tournamentId && currentTournament.id !== data.tournamentId) return;
    // Don't change status to 'finished' if a game is in progress
    // Let the game complete first
    set({
      status: status === 'playing' ? 'playing' : 'finished',
      finalStandings: data.standings,
      winner: data.winner,
      standings: data.standings,
      /*
       * Il torneo che teniamo in memoria deve sapere di essere finito.
       *
       * Non e' una rifinitura: il polling della lobby gira ogni 30 secondi
       * finche' `currentTournament.status === 'active'`, e nessuno aggiornava
       * quel campo. Cosi' entro mezzo minuto dall'annuncio la classifica FINALE
       * — quella con gli spareggi — veniva riscritta dalla risposta REST, senza
       * bisogno di ricaricare la pagina. Chiuso il rubinetto, la classifica
       * annunciata resta quella annunciata.
       */
      currentTournament: currentTournament
        ? {
            ...currentTournament,
            status: 'finished' as const,
            /*
             * E anche CHI HA VINTO, che e' la stessa cosa e per lo stesso
             * motivo.
             *
             * La fascia d'oro legge `currentTournament.winnerUsername`, che
             * normalmente arriva dalla chiamata REST del montaggio. Ma chi sta
             * gia' sulla pagina quando il torneo finisce non rifa' nessuna
             * chiamata — il `useEffect` dipende da `tournamentId`, e il
             * polling della lobby si ferma appena lo stato non e' piu'
             * `active` — quindi quel campo restava com'era: vuoto. Risultato:
             * «nessun vincitore» proprio a chi ha seguito il torneo fino alla
             * fine, finche' non ricaricava.
             *
             * Il nome ce l'abbiamo gia' in mano: `data.winner` e' lo stesso
             * che il server ha appena scritto in archivio.
             *
             * Un `winner` nullo NON cancella un nome che la pagina aveva gia'.
             * L'annuncio puo' arrivare senza vincitore per ragioni che non
             * significano «non c'e'»: una chiusura concorrente ha scritto lei
             * la riga, la scrittura e' fallita per un intoppo passeggero,
             * oppure e' un evento in ritardo per un torneo di cui avevamo gia'
             * letto il vincitore via REST. In tutti e tre i casi sovrascrivere
             * con `null` vorrebbe dire togliere dallo schermo un campione
             * giusto — e senza che nessun evento successivo lo rimetta.
             * Aggiungere un nome si puo', toglierlo no.
             */
            winnerUsername: data.winner?.username ?? currentTournament.winnerUsername ?? null,
          }
        : currentTournament,
    });
  },

  rientraInPartita: (data) => {
    /*
     * Il rientro delle partite di TORNEO finiva nello store del multiplayer.
     *
     * `connection.handler` manda `tournamentId` accanto ad `arenaId` da sempre,
     * ma solo l'arena aveva il suo ramo: ricaricando la pagina durante la
     * partita del turno 2 si ripartiva sulla schermata online normale — niente
     * intestazione del torneo, niente punti, niente posizione, chat persa — e a
     * fine partita non si tornava alla lobby. Peggio: lo store del torneo non
     * sapeva nemmeno che quella partita fosse finita, e al turno dopo
     * l'accoppiamento arrivava a uno schermo che non lo ascoltava.
     */
    log.info('Tournament', 'rientro in partita di torneo', { tournamentId: data.tournamentId, roomId: data.roomId });
    void get().loadTournament(data.tournamentId);

    /*
     * Un rientro SENZA NOME non cancella l'avversario che la pagina ha già.
     *
     * Il nome dell'avversario di torneo arriva in `tournament:match`; il
     * rientro lo riporta leggendolo dal POSTO in memoria del server
     * (`room.getOpponent`), che non ha nessun ripiego: se quel posto non porta
     * il nome, il payload arriva senza, i gate di `multiplayer-store` lo
     * trasformano in `null`, e questa riga lo scriveva sopra a un'identità
     * completa. A schermo: «Anonimo» e il punteggio a trattino per tutto il
     * resto della partita, su una damiera che per il resto funziona — perché
     * nome e punteggio vengono dallo stesso oggetto (`TournamentGame`).
     *
     * Il gemello online conserva già così (`applyReconnectResult`, ramo della
     * partita finita mentre eri via); i rami di torneo e arena cancellavano.
     *
     * Solo a parità di STANZA, e non è un dettaglio: l'avversario del turno
     * prima non deve sopravvivere a un accoppiamento nuovo. Un rientro su una
     * stanza diversa da quella che si ha in mano azzera, come prima.
     */
    const stessaStanza = data.roomId === get().currentRoomId;
    const avversario = data.opponent ?? (stessaStanza ? get().opponent : null);
    if (!data.opponent) {
      log.warn('Tournament', 'rientro senza il nome dell\'avversario', {
        tournamentId: data.tournamentId,
        roomId: data.roomId,
        stessaStanza,
        conservato: !!avversario,
      });
    }

    // Stessa stanza di prima: il contesto della chat si RIPRISTINA, non si azzera.
    useChatStore.getState().setChatContext({
      roomId: data.roomId,
      playerColor: data.color,
      canChat: true,
      source: 'tournament',
    });
    if (data.chat) useChatStore.getState().restoreMessages(data.chat);

    set({
      status: 'playing',
      isJoined: true,
      currentGameId: data.roomId,
      currentRoomId: data.roomId,
      boardState: data.state,
      playerColor: data.color,
      opponent: avversario,
      // L'ultima mossa resta evidenziata: azzerarla toglieva la casella accesa a
      // chi ricarica. L'arena la ricava dalla storia allo stesso modo.
      lastMove: data.state.moveHistory?.at(-1) ?? null,
      preMoveBoardState: null,
      gameResult: null,
      gameEndReason: null,
      // Un accoppiamento vecchio non deve sopravvivere al rientro.
      matchFoundData: null,
      /* L'apertura sorteggiata, che il rientro ora porta (vedi `campoApertura`
         in connection.handler): senza, chi ricaricava perdeva l'intestazione
         «APERTURA N° …» e i grigi in Mosse, e le semi-mosse del sorteggio
         contavano come mosse giocate (`openingPlyCount` qui deriva da
         `moves.length`). Mai quella di un turno prima: viene dal payload della
         stanza in cui si rientra. Stesso oggetto se e' la stessa apertura della
         stessa stanza, cosi' il cartellino non si riaccende a ogni rientro. */
      aperturaDellaPartita: aperturaDalRientro(
        data.opening,
        stessaStanza ? get().aperturaDellaPartita : null,
      ),
      /* La manche, che il rientro ora porta (`campoManche`). Un payload che non
         la porta — server piu' vecchio del campo — non cancella quella che la
         pagina ha gia', purche' sia la stessa stanza: stesso patto
         dell'apertura qui sopra. */
      mancheDellaPartita: data.manche?.leg ?? (stessaStanza ? get().mancheDellaPartita : null),
      mancheTotaliDellaPartita:
        data.manche?.totalLegs ?? (stessaStanza ? get().mancheTotaliDellaPartita : null),
      ratingChange: null,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
      /*
       * Le negoziazioni pendenti e il freno della patta tornano col rientro.
       *
       * Il colore identifica l'offerente: le offerte vivono sul POSTO, non sul
       * socket, che al rientro cambia. Un payload che non li porta non cancella
       * niente — e il freno, se manca, resta quello che si ha: mai spegnere il
       * bottone per un campo assente.
       */
      drawOffered: data.drawOfferBy === (data.color === 'white' ? 'WHITE' : 'BLACK'),
      opponentOfferedDraw: data.drawOfferBy != null
        && data.drawOfferBy !== (data.color === 'white' ? 'WHITE' : 'BLACK'),
      ...(data.offertePatta
        ? { drawOffersLeft: data.offertePatta.restanti, drawMustMove: data.offertePatta.deviMuovere }
        : {}),
    });
    /*
     * L'orologio PRIMA di `syncClock`, non dopo.
     *
     * `syncClock` legge l'incremento da `clockState`, che dopo una ricarica e'
     * nullo: chiamarlo e basta rimetteva l'incremento a ZERO, e da li' in poi
     * ogni mossa non veniva piu' pagata. Il payload del rientro la cadenza ce
     * l'ha — e' per questo che il server la manda.
     */
    const cadenza = data.timeControl ? resolveTimeControl(data.timeControl) : null;
    if (data.clock && cadenza && data.timeControl) {
      get().initClock({
        white: data.clock.white,
        black: data.clock.black,
        increment: cadenza.increment,
        timeControl: data.timeControl as TimeControlKey,
      });
      get().syncClock(data.clock);
    } else if (data.clock) {
      get().syncClock(data.clock);
    }
  },

  mostraPartitaFinitaMentreEriVia: (data) => {
    log.info('Tournament', 'partita finita mentre eri via', {
      tournamentId: data.tournamentId, roomId: data.roomId, result: data.result, reason: data.reason,
    });
    /* Le stesse due strade di una partita vissuta fino in fondo: il rientro
       rimette posizione, colore, avversario e chat, la fine mette esito,
       motivo e lo stato di attesa del turno. Senza orologio: non c'e' piu'
       niente che corra. */
    get().rientraInPartita({ ...data, clock: null });
    get().setGameEnd(data.result, data.reason);
    set({ partitaFinitaDaMostrare: true });
  },

  consumaPartitaFinitaDaMostrare: () => {
    set({ partitaFinitaDaMostrare: false });
  },

  setGameStart: (state: BoardState, clock: GameClockInit | null) => {
    log.info('Tournament', 'game start event received');
    const currentStatus = get().status;
    set({
      /* Il ritorno e' COMINCIATO: l'annuncio che lo aspettava ha finito il suo
         lavoro. Lasciarlo acceso vorrebbe dire un conto alla rovescia che
         scorre sopra una partita gia' in corso. */
      ritornoInArrivo: null,
      boardState: state,
      lastMove: null,
      preMoveBoardState: null,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
      /* Il turno nuovo e' una PARTITA nuova: quota di proposte piena. Senza,
         chi le aveva esaurite al turno 2 ripartiva col bottone spento al 3. */
      drawOffersLeft: MAX_OFFERTE_PATTA_PER_PARTITA,
      drawMustMove: false,
      // Auto-transition to playing for Swiss (no MatchFoundOverlay countdown)
      ...(currentStatus === 'match-found' ? { status: 'playing' as const, matchFoundData: null } : {}),
    });
    /* Lo svizzero non monta l'overlay, che era l'unica sorgente del suono:
       senza questa riga il turno nuovo cominciava muto. */
    const roomKey = get().currentRoomId;
    playSound('match-found', roomKey ? { onceForKey: roomKey } : undefined);
    // Initialize clock if provided
    if (clock) {
      get().initClock(clock);
    }
  },

  setGameUpdate: (state: BoardState, lastMove: Move, clock: ClockSyncEvent | null, roomId?: string) => {
    /*
     * La stanza si guarda anche qui, non solo a monte.
     *
     * `multiplayer-store` instrada per `roomId` e sbaglia solo quando il server
     * quel campo non lo manda (finestra di deploy), dove ricade sull'euristica
     * degli stati. Questa riga e' il fermo di sicurezza di quel ripiego: senza,
     * un aggiornamento di un'ALTRA partita arrivato qui per errore si scriveva
     * su questa damiera senza che niente lo confrontasse con la nostra stanza.
     */
    if (roomId && roomId !== get().currentRoomId) {
      log.warn('Tournament', 'setGameUpdate scartato: non e la nostra stanza', { roomId, nostra: get().currentRoomId });
      return;
    }
    // A non-null snapshot means this echoes our own optimistic move — makeMove
    // already played the sound instantly, so skip it here to avoid a delayed
    // double sound. Opponent moves have no snapshot and still sound on arrival.
    const wasOptimistic = get().preMoveBoardState !== null;
    set({
      boardState: state,
      lastMove,
      preMoveBoardState: null,
      /* Ho mosso io? Allora posso riproporre patta: vedi `utils/sblocco-patta.ts`. */
      ...sbloccoPattaDopoMossa(state.currentTurn, get().playerColor, get().drawMustMove),
    });
    // Sync clock if provided
    if (clock) {
      get().syncClock(clock);
    }
    if (lastMove && !wasOptimistic) {
      // isPromotion is stripped from lastMove by the server schema; read it from
      // the authoritative broadcast board state (server pushes the resolved move).
      // `isCapture`: una presa suona SEMPRE `move`, mai `promote`. Quando e' una presa
      // multipla questo e' il SALTO 1 — il replay del fantasma (`syncFromExternal`) suona
      // i successivi e la promozione sull'ultimo. Le due chiamate cadono a un fotogramma
      // di distanza sullo stesso elemento audio in cache, quindi si sente un suono solo:
      // e' proprio la regola «presa → move» a renderlo innocuo.
      const ultima = state.moveHistory.at(-1);
      playMoveSound(ultima?.isPromotion ?? false, 'auto', (ultima?.captures.length ?? 0) > 0);
      vibrate(40);
    }
  },

  rollbackOptimisticMove: (message: string) => {
    // Server rejected our optimistic move (rare desync). Restore the pre-move
    // board so it doesn't stay stuck showing a move the server never accepted.
    /*
     * `message` è la frase inglese di `game:error`, scritta per i log: il codice —
     * l'unica cosa stabile — si ferma in `multiplayer-store.ts` e qui non arriva. Ma
     * di codici che passano da questa funzione ce n'è UNO solo: `MOVE_REJECTED`,
     * l'unico per cui quel file la chiama. Quello si può dire in tutte e otto le
     * lingue; la frase resta nel log. Identico alla gemella d'arena.
     */
    log.warn('Tournament', 'mossa rifiutata dal server', { message });
    const chiave = 'tournament.errors.moveRejected';
    const { preMoveBoardState } = get();
    if (preMoveBoardState) {
      set({ boardState: preMoveBoardState, preMoveBoardState: null, error: chiave });
      return;
    }
    set({ error: chiave });
  },

  rilasciaPartita: () => {
    /*
     * Il server ha detto che quella partita non esiste piu' (riavvio, deploy:
     * le partite vivono solo in RAM). Senza questo rilascio lo stato restava
     * 'playing' PER SEMPRE, perche' la mp-store delega `game:start` al torneo e
     * quindi il suo `boardState` e' nullo: nessuna delle sue pulizie scatta.
     *
     * Con la navigazione allargata quello stato appiccicato non e' piu' un
     * fastidio confinato a due schermate: ributta il giocatore su una damiera
     * congelata da QUALUNQUE pagina, ogni volta che prova ad andarsene, e
     * dirotta al torneo anche gli aggiornamenti di una partita successiva.
     *
     * Si rilascia solo una partita che si sta GIOCANDO: 'match-found' e la
     * schermata di fine partita non sono il difetto da chiudere.
     */
    if (get().status !== 'playing' || !get().boardState || get().gameResult) return;
    /* Con la stanza e l'avversario che si stanno buttando: e' la riga che
       spiegherebbe un «Anonimo» comparso senza ricarica, perche' questo rilascio
       azzera l'avversario insieme a tutto il resto. Chi decide SE rilasciare e'
       `applyReconnectResult` (vedi `haGuardatoDavvero`). */
    log.warn('Tournament', 'partita di torneo rilasciata: il server non la conosce piu\'', {
      roomId: get().currentRoomId,
      avversario: get().opponent?.username ?? null,
      mosse: get().boardState?.moveHistory?.length ?? 0,
    });
    useChatStore.getState().clearChatContext();
    set({
      /*
       * E si DICE perche', invece di teletrasportare in silenzio.
       *
       * `App` porta via da qui — la seconda meta' di G6 — e senza una parola
       * la damiera sparirebbe sotto le dita: identico a un guasto. Il canale
       * e' quello che c'e' gia': `error` e' una chiave i18n, e la rendono sia
       * la lobby sia la scheda del torneo, cioe' le due destinazioni possibili.
       * L'avviso del gemello online (`AvvisoRientro`) non serve: vive nel solo
       * ramo della home e in lobby non si vedrebbe.
       */
      error: 'tournament.errors.partitaSvanita',
      status: 'waiting',
      currentGameId: null,
      currentRoomId: null,
      boardState: null,
      preMoveBoardState: null,
      lastMove: null,
      playerColor: null,
      opponent: null,
      gameResult: null,
      gameEndReason: null,
      clockState: null,
      drawOffered: false,
      opponentOfferedDraw: false,
      drawDeclinedNotice: false,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
    });
  },

  setGameEnd: (result: 'white' | 'black' | 'draw', reason: string, myRatingChange?: { oldRating: number; newRating: number }) => {
    log.info('Tournament', 'game end event received', { result, reason });
    const { finalStandings } = get();
    // If tournament:ended arrived during game, go to 'finished' instead of 'waiting'
    const nextStatus = finalStandings ? 'finished' : 'waiting';
    set({
      gameResult: result,
      gameEndReason: reason,
      ratingChange: myRatingChange || null,
      status: nextStatus,
      // Clear the optimistic snapshot so a finished game never leaves a dangling
      // preMoveBoardState that could mute a later sound or hijack a game:error.
      preMoveBoardState: null,
      currentGameId: null,
      currentRoomId: null,
      clockState: null, // Clear clock on game end
      drawOffered: false,
      opponentOfferedDraw: false,
      drawDeclinedNotice: false,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
      // Don't reset boardState here - needed for showing final position
    });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  setTournamentGameCompleted: (game: TournamentGameEntry, tournamentId?: string) => {
    if (!riguardaIlTorneoInPagina(get().currentTournament, tournamentId)) return;
    get().addCompletedGame(game);
  },

  /*
   * Il torneo e' stato annullato.
   *
   * Due cose, e nessuna delle due e' una ricarica dalla rete: la card sparisce
   * dalla lista, e chi e' sulla PAGINA di quel torneo se lo sente dire invece di
   * restare a guardare un conto alla rovescia verso un inizio che non arrivera'.
   *
   * La frase e' una chiave i18n e non una stringa: `error` viene reso con `t()`,
   * ed e' lo stesso banco d'uscita del «torneo al completo» — che offre anche la
   * strada verso gli altri tornei.
   */
  setTournamentCancelled: (tournamentId: string, reason: 'organizer' | 'no_players') => {
    set((state) => {
      const eraQuello = state.currentTournament?.id === tournamentId;
      return {
        tournaments: state.tournaments.filter((t) => t.id !== tournamentId),
        currentTournament: eraQuello && state.currentTournament
          ? { ...state.currentTournament, status: 'cancelled' as const }
          : state.currentTournament,
        error: eraQuello ? chiaveDiAnnullamento(reason) : state.error,
      };
    });
  },

  updateTournamentPlayerCount: (tournamentId: string, playerCount: number) => {
    set((state) => ({
      tournaments: state.tournaments.map((t) =>
        t.id === tournamentId ? { ...t, playerCount } : t
      ),
      currentTournament: state.currentTournament?.id === tournamentId
        ? { ...state.currentTournament, playerCount }
        : state.currentTournament,
    }));
  },
}));

/**
 * I callback degli eventi della STANZA di un torneo (`tournament:<id>`):
 * classifica, avvio, turni, avanzamento, partite concluse, iscritti, fine,
 * annullamento.
 *
 * Li registrano DUE store: il multiplayer, che si connette al boot per ogni
 * utente autenticato, e questo, nel suo `connect()` — che parte solo per chi si
 * iscrive o e' iscritto a un torneo in corso. Chi GUARDA una scheda senza
 * esserne iscritto aveva quindi solo le chiavi del multiplayer, che erano meno:
 * niente annullamento, niente inizio e fine turno, niente avanzamento. La
 * scheda restava «in programma» a torneo annullato.
 *
 * Il registro dei callback e' unito PER CHIAVE e chi registra per ultimo
 * vince: due versioni diverse della stessa chiave volevano dire un
 * comportamento che cambiava con l'ordine di connessione. Una definizione sola,
 * spalmata da entrambi, toglie il problema alla radice.
 */
export function callbackDellaStanzaTorneo(): Pick<Parameters<typeof initSocket>[0],
  | 'onTournamentStandings' | 'onTournamentStarted' | 'onTournamentEnded'
  | 'onTournamentGameCompleted' | 'onTournamentListUpdate' | 'onTournamentCancelled'
  | 'onTournamentRoundStart' | 'onSwissRoundProgress' | 'onTournamentRoundEnd'> {
  const stato = () => useTournamentStore.getState();
  return {
    onTournamentStandings: (data) => {
      stato().setStandingsUpdate(data);
    },
    onTournamentStarted: (data?: Partial<TournamentStartedData>) => {
      stato().setTournamentStarted(data?.tournamentId, data?.totalRounds, { format: data?.format, twoLeg: data?.twoLeg });
    },
    onTournamentEnded: (data) => {
      stato().setTournamentEnded(data);
    },
    onTournamentGameCompleted: (data) => {
      stato().setTournamentGameCompleted(data.game, data.tournamentId);
    },
    onTournamentListUpdate: (data) => {
      stato().updateTournamentPlayerCount(data.tournamentId, data.playerCount);
    },
    onTournamentCancelled: (data) => {
      stato().setTournamentCancelled(data.tournamentId, data.reason);
    },
    onTournamentRoundStart: (data: { roundNumber: number; totalRounds: number; tournamentId?: string }) => {
      stato().setRoundStart(data.roundNumber, data.totalRounds, data.tournamentId);
    },
    onSwissRoundProgress: (data) => {
      stato().setAvanzamentoTurno(data.roundNumber, data.completedGames, data.totalGames, data.tournamentId);
    },
    onTournamentRoundEnd: (data) => {
      stato().setRoundEnd(data.roundNumber, data.tournamentId);
      /*
       * La classifica che arriva con la fine del turno, che prima si buttava.
       *
       * E' l'unica con gli spareggi RICALCOLATI: il server li rifa' a fine
       * turno (`recalculateTiebreakers`) e li manda solo qui. Scartandola,
       * Buchholz e Sonneborn-Berger nuovi comparivano soltanto alla prima
       * partita chiusa del turno dopo. Passa dallo stesso ingresso di
       * `tournament:standings`, cosi' vale lo stesso filtro (torneo in
       * pagina / torneo in cui gioco); il numero di iscritti a fine turno
       * non cambia, e si tiene quello che c'e'.
       */
      if (data.tournamentId && Array.isArray(data.standings)) {
        stato().setStandingsUpdate({
          tournamentId: data.tournamentId,
          standings: data.standings,
          participantCount: stato().participantCount,
          myRank: null,
          myStats: null,
        });
      }
    },
  };
}
